import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MessageSquarePlus } from 'lucide-react';
import { setLlmMessageFeedbackApi, uploadProjectAttachmentApi } from '../../api/chat.api';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { useOpenSettings } from '../../features/settings/SettingsModalContext';
import { revertChatMessage, sendMessage, setMessageFeedback, clearGenerationPhase } from '../../redux/slices/chatSlice';
import { createProjectChat, deleteProjectChat, updateProjectChat } from '../../redux/slices/chatsSlice';
import { DEFAULT_LLM_MODEL_KEY, getLlmModelByKey, LLM_MODELS } from '../../constants/llmModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';
import { DEFAULT_LLM_PROVIDER_KEY } from '../../constants/llmProviders';
import { classNames } from '../../utils/classNames';
import { uploadReferenceImages, listImageFiles, REFERENCE_IMAGE_MAX_COUNT } from '../../utils/referenceImages';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ChatComposer } from './ChatComposer';
import { ChatFreeQuotaBanner } from './ChatFreeQuotaBanner';
import { ChatMessageImages } from './ChatMessageImages';
import { DeleteChatModal } from './DeleteChatModal';
import { CreateTaskModal } from './CreateTaskModal';
import { EditChatModal } from './EditChatModal';
import { TaskSwitcher } from './TaskSwitcher';
import { InsufficientBalanceModal } from './InsufficientBalanceModal';
import { RevertMessageModal } from './RevertMessageModal';
import { MessageMarkdown } from './MessageMarkdown';
import {
  AssistantMessageActions,
  UserMessageActions,
  copyTextToClipboard
} from './MessageActions';
import { AskRismanSelection } from './AskRismanSelection';
import { MessageSuggestions } from './MessageSuggestions';
import { LlmGenerationProgress, AiTypingIndicator } from './LlmGenerationProgress';
import { QuotedMessagePreview } from './QuoteBar';
import {
  isProductCreateForbiddenMessage,
  ProductCreateBanner,
  ProductCreateErrorMessage
} from '../projects/ProductCreateBanner';
import { fetchMyPackageApi } from '../../api/packages.api';

const getAssistantDisplayText = (item) => item.message?.trim() || '';

const getUserDisplayText = (item) => item.content?.trim() || '';

const getMessageBody = (item) => (item.role === 'user' ? getUserDisplayText(item) : getAssistantDisplayText(item));

const sentBootstrapMessages = new Set();

const getBootstrapDedupeKey = (projectId, message) => `${projectId}:${message}`;
const buildTaskBootstrapMessage = (title, context) => {
  const trimmedContext = context?.trim() || '';
  const trimmedTitle = title?.trim() || '';
  return trimmedContext || trimmedTitle;
};

export const ChatPanel = ({
  project,
  activeChatId,
  chatsLoading = false,
  onSelectChat,
  bootstrapChat = null
}) => {
  const dispatch = useAppDispatch();
  const openSettings = useOpenSettings();
  const { t, language } = useI18n();
  const sendMessagePromiseRef = useRef(null);
  const { models: catalogModels } = useLlmCatalog();
  const workspaceModels = useMemo(() => {
    const chatModels = catalogModels.filter((item) => (item.category || 'chat') === 'chat');
    return chatModels.length ? chatModels : LLM_MODELS;
  }, [catalogModels]);
  const [message, setMessage] = useState('');
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [suggestionsLocked, setSuggestionsLocked] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [creatingTaskLabel, setCreatingTaskLabel] = useState('');
  const [editChatOpen, setEditChatOpen] = useState(false);
  const [deleteChatOpen, setDeleteChatOpen] = useState(false);
  const [chatActionTarget, setChatActionTarget] = useState(null);
  const [deletingChat, setDeletingChat] = useState(false);
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [attachmentDraftFile, setAttachmentDraftFile] = useState(null);
  const [attachmentDraftPreviewUrl, setAttachmentDraftPreviewUrl] = useState('');
  const [attachmentUploadError, setAttachmentUploadError] = useState('');
  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState('');
  const [insufficientBalanceModal, setInsufficientBalanceModal] = useState({ open: false, message: '' });
  const [messageImagePreview, setMessageImagePreview] = useState('');
  const [quote, setQuote] = useState(null);
  const [voiceError, setVoiceError] = useState('');
  const [entitlement, setEntitlement] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const composerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const loading = useAppSelector((state) => state.chat.loading);
  const historyLoading = useAppSelector((state) => state.chat.historyLoading);
  const chatsError = useAppSelector((state) => state.chats.error);
  const agentError = useAppSelector((state) => state.agent.error);
  const error = useAppSelector((state) => state.chat.error || state.chat.historyError || chatsError);
  const displayedError =
    error || (typeof agentError === 'string' ? agentError : agentError?.message || '');
  const productLocked = Boolean(entitlement) && !entitlement.canCreateProduct;
  const projectChats = useAppSelector((state) => state.chats.byProjectId[project.id] || []);
  const messages = useAppSelector((state) =>
    activeChatId ? state.chat.byChatId[activeChatId] || [] : []
  );
  const generation = useAppSelector((state) =>
    activeChatId ? state.chat.generationByChatId?.[activeChatId] || null : null
  );
  const generationPhase = generation?.phase || null;
  const activeChat = projectChats.find((chat) => chat.id === activeChatId) || null;
  const canSend = Boolean(activeChatId) && !chatsLoading;

  const completedAssistantCount = useMemo(
    () =>
      messages.filter(
        (item) =>
          item.role === 'assistant' && !item.isStreaming && getAssistantDisplayText(item)
      ).length,
    [messages]
  );
  const lastAssistantMessage = useMemo(
    () =>
      [...messages]
        .reverse()
        .find((item) => item.role === 'assistant' && !item.isStreaming && getAssistantDisplayText(item)),
    [messages]
  );
  const generatingAssistant = useMemo(() => {
    const streaming = [...messages].reverse().find((item) => item.role === 'assistant' && item.isStreaming);
    if (streaming) {
      return streaming;
    }
    if (generationPhase) {
      return [...messages].reverse().find((item) => item.role === 'assistant') || null;
    }
    return null;
  }, [generationPhase, messages]);
  const prevCompletedAssistantCountRef = useRef(0);
  const prevMessageCountRef = useRef(0);
  const prevChatIdForScrollRef = useRef(activeChatId);
  const prevHistoryLoadingRef = useRef(historyLoading);
  const bootstrapSentRef = useRef(false);

  useEffect(() => {
    bootstrapSentRef.current = false;
  }, [project.id]);

  const lockSuggestions = useCallback(() => {
    setSuggestionsLocked(true);
  }, []);

  const visibleSuggestions = useMemo(() => {
    const suggestions = lastAssistantMessage?.suggestions || [];

    if (!suggestions.length || suggestionsLocked || generationPhase) {
      return [];
    }

    return suggestions;
  }, [generationPhase, lastAssistantMessage, suggestionsLocked]);

  const handleMessageChange = useCallback((value) => {
    setMessage(value);
  }, []);

  const toggleSuggestion = useCallback((label) => {
    setSelectedSuggestions((current) =>
      current.includes(label) ? current.filter((item) => item !== label) : [...current, label]
    );
  }, []);

  const handleRemoveSuggestion = useCallback((label) => {
    setSelectedSuggestions((current) => current.filter((item) => item !== label));
  }, []);

  const handleAttachmentRemove = useCallback((indexToRemove) => {
    setUploadedAttachments((current) => current.filter((_, index) => index !== indexToRemove));
  }, []);

  const closeAttachmentModal = useCallback(() => {
    if (uploadingAttachment) {
      return;
    }
    setAttachmentModalOpen(false);
    setAttachmentDraftFile(null);
    setAttachmentDraftPreviewUrl('');
    setAttachmentUploadError('');
  }, [uploadingAttachment]);

  const handleConfirmAttachmentUpload = useCallback(async () => {
    if (!project?.id || !attachmentDraftFile || uploadingAttachment) {
      return;
    }

    setUploadingAttachment(true);
    setAttachmentUploadError('');
    try {
      const attachment = await uploadProjectAttachmentApi({ projectId: project.id, file: attachmentDraftFile });
      if (!attachment.url) {
        throw new Error(t('attachmentUploadMissingUrl'));
      }
      setUploadedAttachments((current) => [
        ...current,
        {
          url: attachment.url,
          fileName: attachment.fileName || attachmentDraftFile.name
        }
      ]);
      setAttachmentModalOpen(false);
      setAttachmentDraftFile(null);
      setAttachmentDraftPreviewUrl('');
    } catch (error) {
      setAttachmentUploadError(error?.message || t('attachmentUploadFailed'));
    } finally {
      setUploadingAttachment(false);
    }
  }, [attachmentDraftFile, project?.id, t, uploadingAttachment]);

  useEffect(() => {
    setSuggestionsLocked(false);
    prevCompletedAssistantCountRef.current = 0;
    setUploadedAttachments([]);
    setSelectedSuggestions([]);
    setAttachmentModalOpen(false);
    setAttachmentDraftFile(null);
    setAttachmentDraftPreviewUrl('');
    setAttachmentUploadError('');
    setVoiceError('');
    setRevertTarget(null);
    setRevertError('');
    setInsufficientBalanceModal({ open: false, message: '' });
  }, [activeChatId]);

  useEffect(() => {
    let cancelled = false;
    fetchMyPackageApi()
      .then((current) => {
        if (!cancelled) {
          setEntitlement(current);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntitlement(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (attachmentDraftPreviewUrl) {
        URL.revokeObjectURL(attachmentDraftPreviewUrl);
      }
    };
  }, [attachmentDraftPreviewUrl]);

  useEffect(() => {
    if (completedAssistantCount > prevCompletedAssistantCountRef.current) {
      setSuggestionsLocked(false);
    }

    prevCompletedAssistantCountRef.current = completedAssistantCount;
  }, [completedAssistantCount]);

  useEffect(() => {
    const chatChanged = prevChatIdForScrollRef.current !== activeChatId;
    const historyJustLoaded = prevHistoryLoadingRef.current && !historyLoading;
    const messageCount = messages.length;
    const countIncreased = messageCount > prevMessageCountRef.current;

    prevChatIdForScrollRef.current = activeChatId;
    prevHistoryLoadingRef.current = historyLoading;
    prevMessageCountRef.current = messageCount;

    if (historyLoading || (!countIncreased && !chatChanged && !historyJustLoaded)) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({
      behavior: chatChanged || historyJustLoaded ? 'auto' : 'smooth',
      block: 'end'
    });
  }, [activeChatId, historyLoading, messages.length]);

  useEffect(() => {
    if (generationPhase !== 'ready' || !activeChatId) {
      return undefined;
    }

    const timer = setTimeout(() => {
      dispatch(clearGenerationPhase(activeChatId));
    }, 1400);

    return () => clearTimeout(timer);
  }, [activeChatId, dispatch, generationPhase]);

  const keepSuggestionsVisible = useCallback(() => {
    const scroller = messagesScrollRef.current;
    const suggestions = suggestionsRef.current;
    if (!scroller || !suggestions) {
      return;
    }

    const overflow = suggestions.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
    if (overflow > 0) {
      scroller.scrollTop += overflow + 8;
    }
  }, []);

  useLayoutEffect(() => {
    keepSuggestionsVisible();
  }, [keepSuggestionsVisible, selectedSuggestions]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      keepSuggestionsVisible();
    });
    observer.observe(composer);
    return () => observer.disconnect();
  }, [keepSuggestionsVisible]);

  const dispatchOutgoingMessage = useCallback(
    (outgoingMessage, options = {}) => {
      const model = options.model || DEFAULT_LLM_MODEL_KEY;
      const provider =
        options.provider || getLlmModelByKey(model, workspaceModels)?.provider || DEFAULT_LLM_PROVIDER_KEY;
      const chatId = options.chatId || activeChatId;
      const imageUrls = options.imageUrls || [];
      const quotedText = options.quotedText || quote?.text || null;

      if (!chatId) {
        return;
      }

      lockSuggestions();
      setMessage('');
      setSelectedSuggestions([]);
      setQuote(null);
      setVoiceError('');

      sendMessagePromiseRef.current?.abort?.();
      const promise = dispatch(
        sendMessage({
          chatId,
          projectId: project.id,
          projectName: project.name,
          message: outgoingMessage,
          language,
          model,
          provider,
          imageUrls,
          quotedText
        })
      );
      sendMessagePromiseRef.current = promise;

      void promise.then((action) => {
        if (sendMessage.rejected.match(action) && action.payload?.statusCode === 402) {
          setInsufficientBalanceModal({
            open: true,
            message: action.payload.message || ''
          });
        }
      });
    },
    [
      activeChatId,
      dispatch,
      language,
      lockSuggestions,
      project.id,
      project.name,
      quote,
      workspaceModels
    ]
  );

  const handleAttachmentSelect = useCallback(
    async (filesOrFile, purpose = 'attach') => {
      if (uploadingAttachment || !project?.id) {
        return;
      }

      const files = listImageFiles(filesOrFile instanceof File ? [filesOrFile] : filesOrFile);
      if (!files.length) {
        return;
      }

      const remainingSlots = Math.max(0, REFERENCE_IMAGE_MAX_COUNT - uploadedAttachments.length);
      if (remainingSlots <= 0) {
        setAttachmentUploadError(t('aiReferenceImagesMax', { max: REFERENCE_IMAGE_MAX_COUNT }));
        return;
      }

      const selected = files.slice(0, remainingSlots);
      if (selected.some((file) => !file.type || !file.type.startsWith('image/'))) {
        setAttachmentUploadError(t('attachmentUploadImageOnly'));
        return;
      }

      setUploadingAttachment(true);
      setAttachmentUploadError('');
      try {
        const uploaded = [];
        for (const file of selected) {
          const attachment = await uploadProjectAttachmentApi({ projectId: project.id, file });
          if (!attachment.url) {
            throw new Error(t('attachmentUploadMissingUrl'));
          }
          uploaded.push({
            url: attachment.url,
            fileName: attachment.fileName || file.name,
            purpose
          });
        }

        if (purpose === 'design') {
          const extraUrls = uploadedAttachments
            .map((item) => item?.url?.trim() || '')
            .filter(Boolean);
          const text =
            [selectedSuggestions.join('\n'), message.trim()].filter(Boolean).join('\n\n') ||
            t('designFromUiPrompt');
          dispatchOutgoingMessage(text, {
            imageUrls: [...extraUrls, ...uploaded.map((item) => item.url)]
          });
          setUploadedAttachments([]);
        } else {
          setUploadedAttachments((current) => [...current, ...uploaded]);
        }

        if (files.length > remainingSlots) {
          setAttachmentUploadError(t('aiReferenceImagesMax', { max: REFERENCE_IMAGE_MAX_COUNT }));
        }
      } catch (error) {
        setAttachmentUploadError(error?.message || t('attachmentUploadFailed'));
      } finally {
        setUploadingAttachment(false);
      }
    },
    [dispatchOutgoingMessage, message, project?.id, selectedSuggestions, t, uploadedAttachments, uploadingAttachment]
  );

  useEffect(() => {
    const outgoingMessage = bootstrapChat?.message?.trim();
    const bootstrapImageUrls = bootstrapChat?.imageUrls || [];
    const bootstrapKey = outgoingMessage
      ? getBootstrapDedupeKey(project.id, outgoingMessage)
      : '';

    if (
      bootstrapSentRef.current ||
      (bootstrapKey && sentBootstrapMessages.has(bootstrapKey)) ||
      (!outgoingMessage && !bootstrapImageUrls.length) ||
      !bootstrapChat ||
      !activeChatId ||
      chatsLoading ||
      historyLoading ||
      loading
    ) {
      return;
    }

    bootstrapSentRef.current = true;
    if (bootstrapKey) {
      sentBootstrapMessages.add(bootstrapKey);
    }

    dispatchOutgoingMessage(outgoingMessage, {
      model: DEFAULT_LLM_MODEL_KEY,
      imageUrls: bootstrapImageUrls
    });
  }, [
    activeChatId,
    bootstrapChat,
    chatsLoading,
    dispatchOutgoingMessage,
    historyLoading,
    loading
  ]);

  const closeInsufficientBalanceModal = useCallback(() => {
    setInsufficientBalanceModal({ open: false, message: '' });
  }, []);

  const handleInsufficientBalanceTopUp = useCallback(() => {
    closeInsufficientBalanceModal();
    openSettings('packages');
  }, [closeInsufficientBalanceModal, openSettings]);

  const submitHandler = (event) => {
    event?.preventDefault?.();
    const attachmentUrls = uploadedAttachments
      .map((item) => item?.url?.trim() || '')
      .filter(Boolean);
    const attachedMessageUrls = uploadedAttachments
      .filter((item) => item.purpose !== 'design')
      .map((item) => item?.url?.trim() || '')
      .filter(Boolean);
    const hasDesignAttachment = uploadedAttachments.some((item) => item.purpose === 'design');

    let messageText = [selectedSuggestions.join('\n'), message.trim()].filter(Boolean).join('\n\n');
    if (attachedMessageUrls.length) {
      messageText = [messageText, ...attachedMessageUrls].filter(Boolean).join('\n');
    }
    if (!messageText && hasDesignAttachment) {
      messageText = t('designFromUiPrompt');
    }

    if (
      loading ||
      productLocked ||
      (!messageText && !attachmentUrls.length) ||
      !canSend ||
      uploadingAttachment
    ) {
      return;
    }

    dispatchOutgoingMessage(messageText, { imageUrls: attachmentUrls });
    setUploadedAttachments([]);
  };

  const stopHandler = () => {
    sendMessagePromiseRef.current?.abort?.();
    sendMessagePromiseRef.current = null;
  };

  const handleCreateTask = async (form) => {
    if (creatingTask) {
      return;
    }

    const referenceImages = form.referenceImages || [];

    setCreatingTask(true);
    setCreatingTaskLabel(t('creatingTask'));

    const action = await dispatch(
      createProjectChat({
        projectId: project.id,
        title: form.title,
        context: form.context,
        model: DEFAULT_LLM_MODEL_KEY
      })
    );

    if (!createProjectChat.fulfilled.match(action)) {
      setCreatingTask(false);
      setCreatingTaskLabel('');
      return;
    }

    let imageUrls = [];

    if (referenceImages.length > 0) {
      const uploadResult = await uploadReferenceImages({
        projectId: project.id,
        files: referenceImages,
        onProgress: (current, total) => {
          setCreatingTaskLabel(t('uploadingReferenceImages', { current, total }));
        }
      });
      imageUrls = uploadResult.urls;
    }

    const createdChatId = action.payload.chat.id;
    onSelectChat?.(createdChatId);
    setCreateTaskOpen(false);
    setCreatingTask(false);
    setCreatingTaskLabel('');
    dispatchOutgoingMessage(buildTaskBootstrapMessage(form.title, form.context), {
      chatId: createdChatId,
      model: DEFAULT_LLM_MODEL_KEY,
      imageUrls
    });
  };

  const handleSaveChat = async (form) => {
    const targetChat = chatActionTarget || activeChat;
    if (!targetChat) {
      return;
    }

    const action = await dispatch(
      updateProjectChat({
        projectId: project.id,
        chatId: targetChat.id,
        title: form.title,
        context: form.context
      })
    );

    if (updateProjectChat.fulfilled.match(action)) {
      setEditChatOpen(false);
      setChatActionTarget(null);
    }
  };

  const handleConfirmDeleteChat = async () => {
    const targetChat = chatActionTarget || activeChat;
    if (!targetChat || deletingChat) {
      return;
    }

    setDeletingChat(true);
    const action = await dispatch(
      deleteProjectChat({
        projectId: project.id,
        chatId: targetChat.id
      })
    );
    setDeletingChat(false);

    if (deleteProjectChat.fulfilled.match(action)) {
      setDeleteChatOpen(false);
      setChatActionTarget(null);

      if (action.payload.nextActiveChatId) {
        onSelectChat?.(action.payload.nextActiveChatId);
      }
    }
  };

  const handleMessageFeedback = useCallback(
    async (item, nextFeedback) => {
      if (!activeChatId || !item?.id) {
        return;
      }

      const previousFeedback = item.feedback || null;
      const feedback = previousFeedback === nextFeedback ? null : nextFeedback;
      dispatch(setMessageFeedback({ chatId: activeChatId, messageId: item.id, feedback }));

      try {
        await setLlmMessageFeedbackApi(item.id, feedback);
      } catch (_err) {
        dispatch(
          setMessageFeedback({
            chatId: activeChatId,
            messageId: item.id,
            feedback: previousFeedback
          })
        );
      }
    },
    [activeChatId, dispatch]
  );

  const handleOpenRevertModal = useCallback((messageItem) => {
    if (loading || reverting || historyLoading) {
      return;
    }

    setRevertError('');
    setRevertTarget(messageItem);
  }, [historyLoading, loading, reverting]);

  const handleCloseRevertModal = useCallback(() => {
    if (reverting) {
      return;
    }

    setRevertTarget(null);
    setRevertError('');
  }, [reverting]);

  const handleConfirmRevert = async () => {
    if (!revertTarget?.id || !activeChatId || reverting) {
      return;
    }

    setReverting(true);
    setRevertError('');

    const action = await dispatch(
      revertChatMessage({
        messageId: revertTarget.id,
        chatId: activeChatId,
        projectId: project.id
      })
    );

    setReverting(false);

    if (revertChatMessage.fulfilled.match(action)) {
      setRevertTarget(null);
      setRevertError('');
      setSuggestionsLocked(false);
      return;
    }

    setRevertError(action.error?.message || t('revertMessageFailed'));
  };

  const isLastChat = projectChats.length <= 1;

  return (
    <section className="flex h-full min-h-0 w-full min-w-0 flex-col border-b border-neutral-200 bg-white xl:border-b-0 xl:border-e dark:border-border dark:bg-panel-strong">
      <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-neutral-200 px-2 dark:border-border">
        <TaskSwitcher
          chats={projectChats}
          activeChatId={activeChatId}
          disabled={chatsLoading}
          onSelect={onSelectChat}
          onEdit={(chat) => {
            setChatActionTarget(chat);
            setEditChatOpen(true);
          }}
          onDelete={(chat) => {
            setChatActionTarget(chat);
            setDeleteChatOpen(true);
          }}
        />
        <button
          type="button"
          onClick={() => setCreateTaskOpen(true)}
          disabled={chatsLoading}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text"
        >
          <MessageSquarePlus size={13} />
          <span className="hidden sm:inline">{t('newChat')}</span>
        </button>
      </div>

      <div ref={messagesScrollRef} className="scrollbar-thin flex-1 space-y-4 overflow-y-auto px-4 pt-4 pb-2">
        {chatsLoading || (historyLoading && messages.length === 0) ? (
          <div className="inline-flex items-center gap-2.5 text-sm text-neutral-400">
            <AiTypingIndicator />
            <span>{t('mockTyping')}</span>
          </div>
        ) : null}
        {!chatsLoading && !historyLoading && messages.length === 0 ? (
          <p className="text-sm text-neutral-400">{t('emptyChat')}</p>
        ) : null}
        {messages.map((item) => {
          const isUser = item.role === 'user';
          if (!isUser && item.role !== 'assistant') {
            return null;
          }

          const displayText = getMessageBody(item);
          const messageImageUrls = item.imageUrls || [];
          const hasMessageImages = messageImageUrls.length > 0;
          const streamSource = item.content || item.message || '';
          const isCompletedAssistant =
            !isUser && !item.isStreaming && Boolean(getAssistantDisplayText(item));
          const isGeneratingThis =
            !isUser && Boolean(generationPhase) && generatingAssistant?.id === item.id;
          const showGenerationProgress = isGeneratingThis;
          const revealAssistantMessage =
            !isUser && (!showGenerationProgress || generationPhase === 'ready');
          const showRevert =
            isCompletedAssistant &&
            item.id !== lastAssistantMessage?.id &&
            !loading &&
            !reverting &&
            !historyLoading &&
            !generationPhase;

          return (
            <div
              key={item.id}
              className="flex w-full flex-col items-start"
            >
              {isUser ? <QuotedMessagePreview text={item.quotedText} className="self-start text-start" /> : null}
              <div
                className={classNames(
                  'text-base leading-7',
                  isUser
                    ? 'max-w-[95%] rounded-3xl bg-neutral-100 px-3.5 py-2.5 text-neutral-900 dark:bg-white/[0.08] dark:text-text'
                    : 'w-full px-0.5 py-1 text-neutral-800 dark:text-text'
                )}
              >
                {!isUser ? (
                  <div className="mb-1.5 text-[11px] font-medium text-neutral-400">{t('assistant')}</div>
                ) : null}
                {showGenerationProgress ? (
                  <div className="space-y-4">
                    <LlmGenerationProgress
                      phase={generationPhase}
                      includeSandbox={Boolean(generation?.sandbox)}
                      streamText={streamSource}
                    />
                    {revealAssistantMessage && displayText ? (
                      <div className="llm-message-reveal">
                        <MessageMarkdown content={displayText} />
                      </div>
                    ) : null}
                  </div>
                ) : item.isStreaming ? (
                  <div className="inline-flex items-center gap-2.5 text-xs text-neutral-400">
                    <AiTypingIndicator />
                    <span>{t('mockTyping')}</span>
                  </div>
                ) : (
                  <>
                    {displayText ? (
                      isUser ? (
                        <div className="whitespace-pre-wrap break-words leading-7">{displayText}</div>
                      ) : (
                        <MessageMarkdown content={displayText} />
                      )
                    ) : null}
                    {hasMessageImages ? (
                      <ChatMessageImages
                        urls={messageImageUrls}
                        onPreview={setMessageImagePreview}
                        className={displayText ? 'mt-2' : undefined}
                        compact={isUser}
                      />
                    ) : null}
                  </>
                )}
              </div>
              {isUser && !item.isStreaming ? (
                <UserMessageActions
                  onCopy={() => copyTextToClipboard(displayText)}
                  onEdit={() => {
                    setMessage(displayText);
                    setQuote(item.quotedText ? { text: item.quotedText, messageId: item.quotedMessageId } : null);
                  }}
                />
              ) : null}
              {!isUser && isCompletedAssistant && !showGenerationProgress ? (
                <AssistantMessageActions
                  feedback={item.feedback}
                  disabled={loading || String(item.id).startsWith('temp')}
                  showCopy={false}
                  showRegenerate={false}
                  onRevert={showRevert ? () => handleOpenRevertModal(item) : undefined}
                  onLike={() => handleMessageFeedback(item, 'like')}
                  onDislike={() => handleMessageFeedback(item, 'dislike')}
                />
              ) : null}
              {!isUser && isCompletedAssistant && item.id === lastAssistantMessage?.id ? (
                <MessageSuggestions
                  ref={suggestionsRef}
                  suggestions={visibleSuggestions}
                  selected={selectedSuggestions}
                  onToggle={toggleSuggestion}
                  disabled={loading}
                />
              ) : null}
            </div>
          );
        })}
        {displayedError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-800 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
            {isProductCreateForbiddenMessage(displayedError) ? (
              <ProductCreateErrorMessage />
            ) : (
              displayedError
            )}
          </div>
        ) : null}
        <AskRismanSelection
          containerRef={messagesScrollRef}
          enabled={!loading}
          onAsk={(text) => {
            setQuote({ text, messageId: null });
          }}
        />
        <div ref={messagesEndRef} aria-hidden="true" />
      </div>

      <div ref={composerRef} className="shrink-0">
        <ProductCreateBanner entitlement={entitlement} />
        <ChatComposer
          message={message}
          onMessageChange={handleMessageChange}
          selectedSuggestions={selectedSuggestions}
          onRemoveSuggestion={handleRemoveSuggestion}
          loading={loading}
          uploadingAttachment={uploadingAttachment}
          canSend={canSend && !productLocked}
          attachments={uploadedAttachments}
          onAttachmentSelect={handleAttachmentSelect}
          onAttachmentRemove={handleAttachmentRemove}
          onSubmit={submitHandler}
          onStop={stopHandler}
          quote={quote}
          onClearQuote={() => setQuote(null)}
          voiceError={voiceError || attachmentUploadError}
          onVoiceError={setVoiceError}
        />
        <div className="px-3 pb-3">
          <ChatFreeQuotaBanner entitlement={entitlement} />
        </div>
      </div>

      <EditChatModal
        open={editChatOpen}
        chat={chatActionTarget}
        onClose={() => {
          setEditChatOpen(false);
          setChatActionTarget(null);
        }}
        onSave={handleSaveChat}
      />
      <CreateTaskModal
        open={createTaskOpen}
        loading={creatingTask}
        loadingLabel={creatingTaskLabel}
        onClose={() => setCreateTaskOpen(false)}
        onCreate={handleCreateTask}
      />
      <Modal
        open={Boolean(messageImagePreview)}
        onClose={() => setMessageImagePreview('')}
        title={t('chatMessageImagePreviewTitle')}
        panelClassName="max-w-3xl rounded-[1.25rem] p-0"
        contentClassName="mb-0 border-b border-border/70 px-3 py-2"
        titleClassName="text-sm font-semibold"
        closeButtonClassName="h-8 w-8 text-lg"
      >
        {messageImagePreview ? (
          <div className="overflow-hidden bg-slate-50/70 dark:bg-slate-950/35">
            <img
              src={messageImagePreview}
              alt={t('chatAttachmentPreviewAlt')}
              className="max-h-[min(82vh,760px)] w-full object-contain"
            />
          </div>
        ) : null}
      </Modal>
      <Modal
        open={attachmentModalOpen}
        onClose={closeAttachmentModal}
        closeDisabled={uploadingAttachment}
        title={t('attachmentPreviewTitle')}
        panelClassName="max-w-xl rounded-[1.5rem] p-0"
        contentClassName="mb-0 border-b border-border/70 px-5 py-4"
        closeButtonClassName="h-9 w-9"
      >
        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-muted">{t('attachmentPreviewHint')}</p>
          {attachmentDraftPreviewUrl ? (
            <div className="overflow-hidden rounded-xl border border-border/70 bg-slate-50/70 dark:bg-slate-950/35">
              <img
                src={attachmentDraftPreviewUrl}
                alt={attachmentDraftFile?.name || t('chatAttachmentPreviewAlt')}
                className="max-h-[22rem] w-full object-contain"
              />
            </div>
          ) : (
            <div className="rounded-xl border border-amber-300 bg-amber-50/80 px-3 py-2 text-sm text-amber-700 dark:border-amber-700/50 dark:bg-amber-950/35 dark:text-amber-200">
              {t('attachmentUploadImageOnly')}
            </div>
          )}
          {attachmentDraftFile ? <p className="text-xs text-muted">{attachmentDraftFile.name}</p> : null}
          {attachmentUploadError ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50/80 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/25 dark:text-rose-200">
              {attachmentUploadError}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-border/70 px-5 pb-5 pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={closeAttachmentModal} disabled={uploadingAttachment}>
            {t('cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirmAttachmentUpload}
            disabled={uploadingAttachment || !attachmentDraftPreviewUrl}
          >
            {uploadingAttachment ? t('uploadingImage') : t('confirmUploadImage')}
          </Button>
        </div>
      </Modal>
      <DeleteChatModal
        open={deleteChatOpen}
        chat={chatActionTarget}
        loading={deletingChat}
        isLastChat={isLastChat}
        onClose={() => {
          setDeleteChatOpen(false);
          setChatActionTarget(null);
        }}
        onConfirm={handleConfirmDeleteChat}
      />
      <RevertMessageModal
        open={Boolean(revertTarget)}
        loading={reverting}
        error={revertError}
        onClose={handleCloseRevertModal}
        onConfirm={handleConfirmRevert}
      />
      <InsufficientBalanceModal
        open={insufficientBalanceModal.open}
        message={insufficientBalanceModal.message}
        onClose={closeInsufficientBalanceModal}
        onTopUp={handleInsufficientBalanceTopUp}
      />
    </section>
  );
};
